import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from "../axiosConfig";
import './ParticipantList.css';
import { typeIcons } from './Utils';
import Checkbox from './Checkbox';

const ParticipantList = ({ participants = [], onChange, emailMode = false }) => {
  
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [sender, setSender] = useState(null); // { id, type } or null

  // Initialize from props
  useEffect(() => {
    setSelected(participants);
    const initialSender = participants.find(p => p.is_sender);
    console.log('ParticipantList: Initial sender found:', initialSender);
    if (initialSender) setSender({ id: initialSender.id, type: initialSender.type });
  }, [participants]);

  // Autocomplete search
  useEffect(() => {
    const fetchMatches = async () => {
      if (query.length < 2) return setMatches([]);
      try {
        let contacts = [];
        let accounts = [];
        if (emailMode) {
          const contactsRes = await axios.get('/contacts/', { withCredentials: true });
          contacts = contactsRes.data.map(c => ({ ...c, type: 'contact' }));
        } else {
          const [contactsRes, accountsRes] = await Promise.all([
            axios.get('/contacts/', { withCredentials: true }),
            axios.get('/accounts/', { withCredentials: true }),
          ]);
          contacts = contactsRes.data.map(c => ({ ...c, type: 'contact' }));
          accounts = accountsRes.data.map(a => ({ ...a, type: 'account' }));
        }
        const combined = emailMode ? contacts : [...contacts, ...accounts];
        const filtered = combined.filter((p) => {
          const fullName = p.type === 'contact'
            ? `${p.first_name || ''} ${p.last_name || ''}`
            : (p.name_short || '');
          return fullName.toLowerCase().includes(query.toLowerCase());
        });
        setMatches(filtered.slice(0, 5));
      } catch (err) {
        console.error('Autocomplete error:', err);
      }
    };

    const delayDebounce = setTimeout(fetchMatches, 300);
    return () => clearTimeout(delayDebounce);
  }, [query]);

  const addParticipant = (participant) => {
    if (!selected.find(p => p.id === participant.id && p.type === participant.type)) {
      const updated = [...selected, participant];
      setSelected(updated);
      onChange(updated);
    }
    setQuery('');
    setMatches([]);
  };

  const removeParticipant = (id, type) => {
    const updated = selected.filter(p => !(p.id === id && p.type === type));
    
    setSelected(updated);
    if (sender?.id === id && sender?.type === type) setSender(null);
    onChange(updated);
  };

  const toggleSender = (id, type) => {
    if (sender?.id === id && sender?.type === type) {
      setSender(null);
      const updated = selected.map(p => ({ ...p, is_sender: false }));
      onChange(updated);
    } else {
      setSender({ id, type });
      const updated = selected.map(p => ({ ...p, is_sender: p.id === id && p.type === type }));
      onChange(updated);
    }
  };

  return (
    <div className="participant-container">
      <label htmlFor="participant">Add Participant:</label>
      <input autoComplete="off"
        id="participant"
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Type a name or account..."
        className="participant-input"
      />

      {matches.length > 0 && (
        <ul className="participant-matches">
          {matches.map(p => (
            <li
              key={`${p.type}-${p.id}`}
              onClick={() => addParticipant(p)}
              className="participant-match"
            >
              {typeIcons[p.type]} {p.type === 'contact' ? `${p.first_name} ${p.last_name} - ${p.account_name_short}` : p.name_short}
            </li>
          ))}
        </ul>
      )}
      
      {selected.length > 0 && (
        <div className="participant-selected">
          <strong>Selected Participants:</strong>
          <table className="participant-table">
            <thead>
              <tr>
                <th>Name</th>
                {!emailMode && <th>Sender</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {selected.map(p => (
                <tr key={`${p.type}-${p.id}`}>
                  <td className="name-col">
                    <Link to={`/${p.type}?id=${p.id}`} className="participant-link">
                      {typeIcons[p.type]} {p.type === 'contact' ? `${p.first_name} ${p.last_name}` : p.name_short}
                    </Link>
                  </td>
                  {!emailMode && (
                    <td>
                      <Checkbox
                        checked={sender?.id === p.id && sender?.type === p.type}
                        onChange={() => toggleSender(p.id, p.type)}
                        title="Mark as sender"
                        className="sender-checkbox"
                      />
                    </td>
                  )}
                  <td>
                    <button
                      type="button"
                      onClick={() => removeParticipant(p.id, p.type)}
                      className="remove-btn"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ParticipantList;
